/**
 * InsuranceEndorsementModal — Add endorsement to an insurance policy
 *
 * Endorsement schema fields (from schema-compliance.ts):
 *  - endorsementType: addition | deletion | amendment
 *  - endorsementNumber (optional)
 *  - description (required)
 *  - effectiveDate (required)
 *  - premiumAdjustment (optional numeric string)
 *  - documentUrl (optional)
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InsuranceEndorsementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  policyId: string;
  onSuccess?: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ENDORSEMENT_TYPES = [
  { value: "addition", label: "Addition" },
  { value: "deletion", label: "Deletion" },
  { value: "amendment", label: "Amendment" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InsuranceEndorsementModal({
  open,
  onOpenChange,
  policyId,
  onSuccess,
}: InsuranceEndorsementModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Form state
  const [endorsementType, setEndorsementType] = useState("");
  const [endorsementNumber, setEndorsementNumber] = useState("");
  const [description, setDescription] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [premiumAdjustment, setPremiumAdjustment] = useState("");
  const [documentUrl, setDocumentUrl] = useState("");

  // Reset on open
  useEffect(() => {
    if (open) {
      setEndorsementType("");
      setEndorsementNumber("");
      setDescription("");
      setEffectiveDate("");
      setPremiumAdjustment("");
      setDocumentUrl("");
    }
  }, [open]);

  const isValid =
    endorsementType.length > 0 &&
    description.trim().length > 0 &&
    effectiveDate.length > 0;

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
  };

  // ---------------------------------------------------------------------------
  // Mutation
  // ---------------------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        "POST",
        `/api/compliance/insurance/${policyId}/endorsements`,
        {
          endorsementType,
          endorsementNumber: endorsementNumber.trim() || undefined,
          description: description.trim(),
          effectiveDate,
          premiumAdjustment: premiumAdjustment || undefined,
          documentUrl: documentUrl.trim() || undefined,
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Failed to create endorsement");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/compliance/insurance/${policyId}`] });
      toast({ title: "Endorsement added successfully" });
      handleOpenChange(false);
      onSuccess?.();
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!isValid) return;
    createMutation.mutate();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="modal-endorsement-form"
      >
        <DialogHeader>
          <DialogTitle>Add Endorsement</DialogTitle>
          <DialogDescription>
            Add an endorsement, exclusion, or amendment to this policy.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Endorsement Type */}
            <div className="space-y-1.5">
              <Label>
                Type <span className="text-destructive">*</span>
              </Label>
              <Select value={endorsementType} onValueChange={setEndorsementType}>
                <SelectTrigger data-testid="select-endorsement-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {ENDORSEMENT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Endorsement Number */}
            <div className="space-y-1.5">
              <Label htmlFor="endorsement-number">Endorsement Number</Label>
              <Input
                id="endorsement-number"
                data-testid="input-endorsement-number"
                value={endorsementNumber}
                onChange={(e) => setEndorsementNumber(e.target.value)}
                placeholder="e.g. END-001"
              />
            </div>

            {/* Effective Date */}
            <div className="space-y-1.5">
              <Label htmlFor="effective-date">
                Effective Date <span className="text-destructive">*</span>
              </Label>
              <Input
                id="effective-date"
                data-testid="input-effective-date"
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
              />
            </div>

            {/* Premium Adjustment */}
            <div className="space-y-1.5">
              <Label htmlFor="premium-adjustment">Premium Adjustment</Label>
              <Input
                id="premium-adjustment"
                data-testid="input-premium-adjustment"
                value={premiumAdjustment}
                onChange={(e) => setPremiumAdjustment(e.target.value)}
                placeholder="e.g. 250.00"
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="endorsement-description">
              Description <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="endorsement-description"
              data-testid="input-endorsement-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the endorsement, exclusion, or amendment..."
              rows={3}
            />
          </div>

          {/* Document URL */}
          <div className="space-y-1.5">
            <Label htmlFor="endorsement-document-url">Document URL</Label>
            <Input
              id="endorsement-document-url"
              data-testid="input-endorsement-document-url"
              value={documentUrl}
              onChange={(e) => setDocumentUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            data-testid="button-save-endorsement"
            onClick={handleSubmit}
            disabled={!isValid || createMutation.isPending}
          >
            {createMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {createMutation.isPending ? "Saving..." : "Add Endorsement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
